import { useState } from 'react';
import { Plane, Clock, Sun } from 'lucide-react';
import { Field, DropSelect, DateTimeRow, ContactFields, ReturnToggle, VehiclePicker, EstimateCard, ManagedConfirmation, ChoiceTabs, PickerBlock, DetailsHeading } from '@/components/booking/ManagedParts';
import { useManagedBooking } from '@/hooks/useManagedBooking';
import { bt } from '@/i18n/bookingI18n';
import { BUS_TYPES, BUS_AIRPORT_DEST, BUS_HALFDAY, BUS_FULLDAY, money } from '@/data/transportPricing';
import { computeBusEstimate, busPriceFor, validateBus } from '@/components/booking/busLogic';

const OPTIONS = { airport: BUS_AIRPORT_DEST, halfday: BUS_HALFDAY, fullday: BUS_FULLDAY };
const DURATION_EN = { airport: 'Airport Transfer', halfday: 'Half Day (6 hrs)', fullday: 'Full Day (12 hrs)' };
const BUS_PICKER = BUS_TYPES.map((b) => ({ id: b.id, name: b.name, models: `${b.seats}-seat`, pax: `Up to ${b.seats} passengers` }));
const EMPTY_FORM = { name: '', email: '', phone: '', notes: '' };
const EMPTY_TRIP = { destIdx: '', busId: '', returnTrip: false, date: null, time: '' };

function useBusState() {
  const [tripType, setTripType] = useState('airport');
  const [trip, setTrip] = useState(EMPTY_TRIP);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const clearError = (k) => setErrors((p) => ({ ...p, [k]: undefined }));
  const patch = (changes, errorKey) => { setTrip((p) => ({ ...p, ...changes })); if (errorKey) clearError(errorKey); };
  const setField = (k, v) => { setForm((p) => ({ ...p, [k]: v })); clearError(k); };
  const switchTrip = (t) => { setTripType(t); patch({ destIdx: '', ...(t !== 'airport' ? { returnTrip: false } : {}) }); setErrors({}); };
  const reset = () => { setTrip(EMPTY_TRIP); setForm(EMPTY_FORM); setErrors({}); };
  return { tripType, trip, form, errors, setErrors, patch, setField, switchTrip, reset };
}

export function BusBooking({ lang = 'en' }) {
  const TRIP_TYPES = [
    { id: 'airport', label: bt(lang, 'tripAirport'), icon: Plane },
    { id: 'halfday', label: bt(lang, 'tripHalfday'), icon: Clock },
    { id: 'fullday', label: bt(lang, 'tripFullday'), icon: Sun },
  ];
  const s = useBusState();
  const b = useManagedBooking(lang);
  const { tripType, trip, form, errors } = s;

  const options = OPTIONS[tripType];
  const opt = trip.destIdx === '' ? null : options[Number(trip.destIdx)];
  const busIndex = BUS_TYPES.findIndex((x) => x.id === trip.busId);
  const busType = BUS_TYPES[busIndex];
  const isAirport = tripType === 'airport';
  const estimate = computeBusEstimate({ opt, busType, busIndex, isAirport, returnTrip: trip.returnTrip, lang });

  const onSubmit = async () => {
    const e = validateBus({ destIdx: trip.destIdx, busId: trip.busId, date: trip.date, time: trip.time, form, lang });
    s.setErrors(e);
    if (Object.keys(e).length || !estimate) return;
    const ok = await b.submit({
      service_type: 'bus', date: trip.date.toISOString(), time: trip.time, duration: DURATION_EN[tripType],
      pickup_type: 'location', pickup_location: isAirport ? 'Airport / as advised' : 'As per itinerary',
      dropoff_type: 'location', dropoff_location: opt.name.slice(0, 200),
      passengers: String(busType.seats), vehicle: `${busType.name} ${busType.seats}-seat`,
      return_trip: trip.returnTrip, route: estimate.summary, price: estimate.total, ...form,
    });
    if (ok) window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (b.submitted) {
    return (
      <ManagedConfirmation name={form.name} bookingRef={b.bookingRef} lang={lang} onReset={() => { b.reset(); s.reset(); }}
        rows={[{ label: bt(lang, 'coachWord'), value: estimate?.summary || '-' }, { label: bt(lang, 'estimatedTotal'), value: estimate ? money(estimate.total) : '-' }]} />
    );
  }

  return (
    <div data-testid="bus-booking" className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
      <div className="lg:col-span-7 space-y-6">
        <ChoiceTabs options={TRIP_TYPES} value={tripType} onChange={s.switchTrip} testidPrefix="bus-trip" stacked />
        <Field label={isAirport ? bt(lang, 'destination') : bt(lang, 'routeLabel')} error={errors.dest}>
          <DropSelect testid="bus-dest" value={trip.destIdx} onChange={(v) => s.patch({ destIdx: v }, 'dest')} placeholder={bt(lang, 'select')} options={options.map((o, i) => ({ value: String(i), label: `${o.name}${o.hrs ? ` · ${o.hrs}` : ''}` }))} />
        </Field>
        <PickerBlock label={bt(lang, 'busType')} error={errors.bus}>
          <VehiclePicker vehicles={BUS_PICKER} value={trip.busId} onChange={(id) => s.patch({ busId: id }, 'bus')} priceFor={(v) => busPriceFor({ opt, v })} lang={lang} />
        </PickerBlock>
        <DateTimeRow date={trip.date} setDate={(d) => s.patch({ date: d }, 'date')} time={trip.time} setTime={(t) => s.patch({ time: t }, 'time')} errors={errors} lang={lang} />
        {isAirport && <ReturnToggle value={trip.returnTrip} onChange={(v) => s.patch({ returnTrip: v })} lang={lang} />}
        <DetailsHeading lang={lang} />
        <ContactFields form={form} set={s.setField} errors={errors} lang={lang} />
      </div>
      <div className="lg:col-span-5">
        <EstimateCard lines={estimate ? estimate.lines : []} total={estimate ? estimate.total : 0} error={b.error} submitting={b.submitting} onSubmit={onSubmit} disabled={!estimate} lang={lang} note={bt(lang, 'busNote')} />
      </div>
    </div>
  );
}
